import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Globe,
  Loader2,
  CheckCircle2,
  FileText,
  Sparkles,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState(null);
  const [claimable, setClaimable] = useState([]);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    (async () => {
      try {
        const r = await apiClient.get("/dashboard/scans", { withCredentials: true });
        setScans(r.data.scans || []);
      } catch {
        setScans([]);
      }
      try {
        const c = await apiClient.get("/dashboard/claimable-scans", { withCredentials: true });
        setClaimable(c.data.scans || []);
      } catch {
        /* ignore */
      }
    })();
  }, [user, authLoading]);

  const claim = async (scanId) => {
    setClaiming(scanId);
    try {
      await apiClient.post(
        `/auth/link-scan/${scanId}`,
        null,
        { withCredentials: true },
      );
      toast.success("Scan claimed — added to your library.");
      // refresh both lists
      const [s, c] = await Promise.all([
        apiClient.get("/dashboard/scans", { withCredentials: true }),
        apiClient.get("/dashboard/claimable-scans", { withCredentials: true }),
      ]);
      setScans(s.data.scans || []);
      setClaimable(c.data.scans || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim scan");
    } finally {
      setClaiming(null);
    }
  };

  const deleteScan = async (scan) => {
    const ok = window.confirm(
      `Delete the public report for ${scan.domain}?\n\nThis removes it from PrivacyLens permanently — anyone visiting /report/${scan.domain} will see a 404. ` +
      `If you've already shared the report URL anywhere, that link will break.`,
    );
    if (!ok) return;
    try {
      await apiClient.delete(`/scan/${scan.scan_id}`, { withCredentials: true });
      toast.success(`Report for ${scan.domain} deleted`);
      setScans((s) => (s || []).filter((x) => x.scan_id !== scan.scan_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete scan");
    }
  };

  if (authLoading) {
    return (
      <section className="min-h-[60vh] bg-[#0A0A0A] text-white grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0047FF]" />
      </section>
    );
  }

  if (!user) {
    return (
      <section
        className="min-h-[60vh] bg-[#0A0A0A] text-white"
        data-testid="dashboard-login-prompt"
      >
        <div className="max-w-[760px] mx-auto px-6 lg:px-12 py-20">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
            / Dashboard · sign in
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-4">
            Sign in to view your scans.
          </h1>
          <p className="text-white/70 mt-4 max-w-xl leading-relaxed">
            Your purchased compliance documents live in your account, locked
            to your Google identity. We use Google sign-in so you never have
            to remember another password — and only you can ever download the
            PDFs you paid for.
          </p>
          <button
            onClick={() => startLogin("/dashboard")}
            className="mt-8 pl-btn-primary px-6 py-3.5 border border-white inline-flex items-center gap-2"
            data-testid="login-button"
          >
            Sign in with Google
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[70vh] bg-white" data-testid="dashboard-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
              / Your dashboard
            </span>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02] mt-3">
              Welcome back, {user.name?.split(" ")[0] || "there"}.
            </h1>
            <p className="text-[#475569] mt-3 max-w-xl">
              Every scan you've claimed lives here. Documents are accessible
              only with your Google sign-in.
            </p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="pl-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2 border border-black self-start"
            data-testid="new-scan-button"
          >
            <Sparkles className="w-4 h-4" /> Run a new scan
          </button>
        </div>

        {claimable.length > 0 && (
          <div
            className="mt-10 border border-[#0047FF] bg-[#0047FF]/5 p-5"
            data-testid="claimable-scans"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF] mb-2">
              / Unclaimed purchase{claimable.length > 1 ? "s" : ""} on this email
            </div>
            <p className="text-sm text-[#0A0A0A]">
              We found a paid Pro scan tied to <span className="font-mono">{user.email}</span>.
              Claim it to lock the documents to your account.
            </p>
            <div className="mt-3 space-y-2">
              {claimable.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 bg-white border border-[#0047FF] px-4 py-3"
                  data-testid={`claimable-${c.id}`}
                >
                  <div>
                    <div className="font-display text-lg tracking-tight">{c.domain}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                      Score {c.trust_score ?? "—"} · paid {c.pro_email}
                    </div>
                  </div>
                  <button
                    onClick={() => claim(c.id)}
                    disabled={claiming === c.id}
                    className="text-xs font-mono uppercase tracking-widest px-3 py-2 bg-[#0047FF] text-white hover:bg-[#0033CC] disabled:opacity-60 inline-flex items-center gap-1.5"
                    data-testid={`claim-${c.id}`}
                  >
                    {claiming === c.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-3 h-3" />
                    )}
                    Claim
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#475569] mb-3">
            / Your scans
          </div>
          {scans === null ? (
            <div className="py-12 grid place-items-center text-[#475569] text-sm font-mono">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : scans.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid gap-3" data-testid="scans-list">
              {scans.map((s) => (
                <ScanRow key={s.scan_id} scan={s} onDelete={deleteScan} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ScanRow({ scan, onDelete }) {
  const score = scan.trust_score;
  const scoreColor =
    score == null
      ? "bg-[#94A3B8]"
      : score >= 80
        ? "bg-[#10B981]"
        : score >= 60
          ? "bg-[#F59E0B]"
          : "bg-[#EF4444]";
  return (
    <div
      className="border border-[#E2E8F0] bg-white hover:border-black hover:shadow-[4px_4px_0_0_#0A0A0A] transition-all flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`scan-row-${scan.scan_id}`}
    >
      <Link
        to={`/report/${scan.domain}`}
        className="flex-1 p-5 flex items-center gap-4"
      >
        <span
          className={`${scoreColor} text-white font-display text-xl px-3 py-2 leading-none w-16 text-center`}
        >
          {score ?? "—"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#475569]" />
            <span className="font-display text-lg tracking-tight truncate">
              {scan.domain}
            </span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-1">
            {scan.created_at ? new Date(scan.created_at).toLocaleString() : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {scan.pro_unlocked && (
            <span className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest">
              <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> Pro
            </span>
          )}
          {scan.pro_documents_status === "done" && (
            <span className="inline-flex items-center gap-1.5 bg-[#0047FF] text-white px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest">
              <FileText className="w-3 h-3" /> PDFs ready
            </span>
          )}
        </div>
      </Link>
      <div className="px-5 py-3 sm:py-5 sm:pl-0 flex sm:flex-col items-end gap-2 border-t sm:border-t-0 sm:border-l border-[#E2E8F0]">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(scan);
          }}
          className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white inline-flex items-center gap-1.5"
          title="Delete the public report for this scan"
          data-testid={`delete-scan-${scan.scan_id}`}
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border border-dashed border-[#E2E8F0] p-12 text-center"
      data-testid="empty-state"
    >
      <Sparkles className="w-8 h-8 text-[#0047FF] mx-auto" />
      <div className="font-display text-2xl tracking-tight mt-4">
        No scans yet.
      </div>
      <p className="text-[#475569] mt-2 max-w-md mx-auto">
        Run your first scan from the home page — once you upgrade a scan to
        Pro, it'll show up here automatically.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-2 pl-btn-primary px-4 py-2.5 text-sm border border-black"
      >
        Run a scan <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
